<script setup lang="ts">
import { computed, ref } from 'vue'
import { useRouter } from 'vue-router'
import { NButton, NDrawer, NDrawerContent, NInput, NSelect, NTabs, NTabPane, NUpload } from 'naive-ui'
import type { UploadFileInfo } from 'naive-ui'
import { storeToRefs } from 'pinia'
import { useSourcesStore, partitionFiles, filesToRetry, ALLOWED_MIMES, MAX_FILE_BYTES } from '../stores/sources'
import { useIsMobile } from '../composables/useIsMobile'
import { useRestoreFocusOnClose } from '../composables/useRestoreFocusOnClose'
import IngestActivityList from './IngestActivityList.vue'

const show = defineModel<boolean>('show', { required: true })

useRestoreFocusOnClose(show)

const sources = useSourcesStore()
const router = useRouter()
const isMobile = useIsMobile()

// On mobile the drawer occupies the full screen — a fixed 560px drawer
// on a 390px phone viewport was the source of the side-scroll.
const drawerWidth = computed<number | string>(() => (isMobile.value ? '100%' : 560))

const { submitting, submitError } = storeToRefs(sources)

const tab = ref<'ask' | 'file' | 'url'>('ask')
const askContent = ref('')
// Optional caller-supplied write-target pin on the Ask
// surface. Empty string (= placeholder selected) means "no category,
// let the LLM auto-classify" — the existing behaviour.
// Empty string rather than `null` because naive-ui's `SelectOption['value']`
// type is `string | number`; a `null` value here trips vue-tsc's union
// narrowing on `SelectMixedOption` (TS sees `value: null` and resolves
// to `SelectIgnoredOption` which expects a `type` discriminator — the
// bug that broke CI on the first push). The store's submitAsk treats
// empty string the same as undefined: the key is omitted from the POST
// body and the LLM auto-classifies. The five non-empty values mirror
// Wiki::Structure::CATEGORY_MAP exactly.
const askCategory = ref<string>('')
const CATEGORY_OPTIONS = [
  { label: 'Auto (LLM classifies)', value: '' },
  { label: 'Entities', value: 'entities' },
  { label: 'Concepts', value: 'concepts' },
  { label: 'Summaries', value: 'summaries' },
  { label: 'Templates', value: 'templates' },
  { label: 'Memory', value: 'memory' },
]
const urlContent = ref('')
const pickedFiles = ref<File[]>([])
const fileRejections = ref<Array<{ filename: string; reason: string }>>([])

const acceptMime = computed(() => ALLOWED_MIMES.join(','))

const canSubmitAsk = computed(() => askContent.value.trim().length > 0 && !submitting.value)

const canSubmitFile = computed(() => pickedFiles.value.length > 0 && !submitting.value)

const canSubmitUrl = computed(() => urlContent.value.trim().length > 0 && !submitting.value)

async function onSubmitAsk() {
  if (!canSubmitAsk.value) return
  try {
    await sources.submitAsk(askContent.value.trim(), askCategory.value)
    askContent.value = ''
    // Keep the category selection after submit — the user's most likely
    // next intent is "ingest another note in the same bucket."
  } catch {
    /* submitError populated in store */
  }
}

// Cmd+Enter / Ctrl+Enter sends — matches the ThreadDrawer composer and
// every chat UI the user touches daily. Plain Enter stays as newline
// since the textarea autosizes for longer prompts.
function onAskEnter(event: KeyboardEvent) {
  if (event.key !== 'Enter') return
  if (!event.metaKey && !event.ctrlKey) return
  event.preventDefault()
  void onSubmitAsk()
}

async function onSubmitFile() {
  if (!canSubmitFile.value || pickedFiles.value.length === 0) return
  const batch = pickedFiles.value.slice()
  const results = await sources.submitFiles(batch)
  const refusedByBackend = results
    .filter((r) => !r.ok)
    .map((r) => ({ filename: r.file.name, reason: r.error ?? 'submit failed' }))

  // Keep only files that failed at submit so the user can retry them
  // without re-dropping. `filesToRetry` matches on a stable name/size/
  // lastModified key rather than File-reference identity, so the prune
  // survives the store cloning the File. Append any new rejections
  // to the existing client-layer list so the summary stays one place.
  pickedFiles.value = filesToRetry(pickedFiles.value, results)
  if (refusedByBackend.length > 0) {
    fileRejections.value = [...fileRejections.value, ...refusedByBackend]
  }
}

async function onSubmitUrl() {
  if (!canSubmitUrl.value) return
  try {
    await sources.submitUrl(urlContent.value.trim())
    urlContent.value = ''
  } catch {
    /* submitError populated in store */
  }
}

// naive-ui's NUpload emits {fileList} with UploadFileInfo entries; we
// extract the raw File refs and hand them to `partitionFiles` so the
// validation rules stay owned by the store. One invalid file in a
// batched drop doesn't block the valid ones from submitting.
function onFileChange(payload: { fileList: UploadFileInfo[] }) {
  const raw = payload.fileList.map((info) => info.file as File | null | undefined).filter((f): f is File => !!f)
  const { accepted, rejected } = partitionFiles(raw)
  pickedFiles.value = accepted
  fileRejections.value = rejected
}

function clearRejections() {
  fileRejections.value = []
}

function openPath(path: string) {
  // Plain path keeps slashes intact — named routes percent-encode them.
  router.push(`/p/${path}`)
  show.value = false
}
</script>

<template>
  <NDrawer v-model:show="show" :width="drawerWidth" placement="right">
    <NDrawerContent title="Add to brain" :native-scrollbar="false" closable>
      <NTabs v-model:value="tab" type="segment" animated class="ingest-tabs">
        <NTabPane name="ask">
          <template #tab><span data-testid="tab-ask">Ask</span></template>
          <form class="flex flex-col gap-3 pt-3" @submit.prevent="onSubmitAsk">
            <NInput
              v-model:value="askContent"
              type="textarea"
              :autosize="{ minRows: 4, maxRows: 10 }"
              placeholder="Tell me something to remember, or ask about what you already have… (⌘/Ctrl+Enter to send)"
              :input-props="{ spellcheck: true }"
              data-testid="prompt-content"
              @keydown="onAskEnter"
            />
            <!-- Optional pin for the LLM's write target.
                 Default "Auto (LLM classifies)" preserves the existing behaviour. -->
            <NSelect
              v-model:value="askCategory"
              :options="CATEGORY_OPTIONS"
              placeholder="Auto (LLM classifies)"
              size="small"
              data-testid="prompt-category"
            />
            <NButton
              type="primary"
              attr-type="submit"
              :loading="submitting"
              :disabled="!canSubmitAsk"
              data-testid="prompt-submit"
            >
              Send to superbrain
            </NButton>
            <p v-if="submitError" class="text-sm text-red-300 m-0">{{ submitError }}</p>
          </form>
        </NTabPane>

        <NTabPane name="file">
          <template #tab><span data-testid="tab-file">File</span></template>
          <div class="flex flex-col gap-3 pt-3">
            <NUpload
              :accept="acceptMime"
              multiple
              :default-upload="false"
              :show-file-list="true"
              data-testid="file-drop"
              @change="onFileChange"
            >
              <div class="border border-dashed border-white/20 rounded p-6 text-center text-sm text-white/60">
                Drop one or more <code>.pdf</code>, <code>.md</code>, or <code>.txt</code> files — or click to pick.
                <br />
                <span class="text-xs text-white/40">Up to {{ MAX_FILE_BYTES / 1_048_576 }} MB each.</span>
              </div>
            </NUpload>

            <NButton
              type="primary"
              :loading="submitting"
              :disabled="!canSubmitFile"
              data-testid="file-submit"
              @click="onSubmitFile"
            >
              {{ pickedFiles.length > 1 ? `Upload ${pickedFiles.length} files` : 'Upload file' }}
            </NButton>

            <section
              v-if="fileRejections.length"
              class="flex flex-col gap-1 text-sm text-red-300"
              data-testid="file-rejections"
            >
              <div class="flex items-center justify-between">
                <span
                  >{{ fileRejections.length }} file{{ fileRejections.length === 1 ? '' : 's' }} couldn't be
                  uploaded:</span
                >
                <NButton size="tiny" quaternary @click="clearRejections">Dismiss</NButton>
              </div>
              <ul class="list-disc pl-5 m-0">
                <li v-for="r in fileRejections" :key="r.filename">
                  <span class="font-mono text-xs">{{ r.filename }}</span>
                  — {{ r.reason }}
                </li>
              </ul>
            </section>
            <p v-if="submitError" class="text-sm text-red-300 m-0">{{ submitError }}</p>
          </div>
        </NTabPane>

        <NTabPane name="url">
          <template #tab><span data-testid="tab-url">URL</span></template>
          <form class="flex flex-col gap-3 pt-3" @submit.prevent="onSubmitUrl">
            <NInput v-model:value="urlContent" placeholder="https://example.com/paper.pdf" data-testid="url-content" />
            <NButton
              type="primary"
              attr-type="submit"
              :loading="submitting"
              :disabled="!canSubmitUrl"
              data-testid="url-submit"
            >
              Fetch and ingest
            </NButton>
            <p v-if="submitError" class="text-sm text-red-300 m-0">{{ submitError }}</p>
          </form>
        </NTabPane>
      </NTabs>

      <!-- Activity list: shared across all tabs, extracted to
           IngestActivityList (#1099) so this view stays under the
           300-line hard limit. -->
      <IngestActivityList @open-path="openPath" />
    </NDrawerContent>
  </NDrawer>
</template>

<style scoped>
/* WCAG 2.5.5 — touch targets at least 44×44 CSS pixels. The default
   segment-tab height (~32px on Naive UI 2.44) is comfortably tappable
   on a mouse but skirts the WCAG floor on mobile, especially when each
   tab takes ~30% of a 390px viewport. Bumping font-weight helps the
   labels read against the segment background at the larger height. */
.ingest-tabs :deep(.n-tabs-tab) {
  min-height: 44px;
  font-weight: 500;
}
</style>
